/** @type {import('tailwindcss').Config} */
module.exports = {
    darkMode: ["selector", '[data-theme="dark"]'],
    content: ["./pages/**/*.{ts,tsx}", "./components/**/*.{ts,tsx}", "./app/**/*.{ts,tsx}", "./src/**/*.{ts,tsx}", "./forms/**/*.{ts,tsx}"],
    future: {
        // Only apply hover styles on devices that support hover (ie, not on mobile devices)
        // https://github.com/tailwindlabs/tailwindcss/pull/8394
        hoverOnlyWhenSupported: true
    },
    theme: {
        container: {
            center: true,
            padding: "2rem",
            screens: {
                "2xl": "1400px"
            }
        },
        fontFamily: {
            sans: ["Poppins", "sans-serif"]
        },
        colors: {
            // Text colors. Ex: text-content-success
            content: {
                current: "currentColor",
                primary: "var(--text-primary)",
                "primary-invert": "var(--text-primary-invert)",
                secondary: "var(--text-secondary)",
                brand: "var(--text-brand)",
                "brand-tertiary": "var(--text-brand-tertiary)",
                "brand-b": "var(--text-brand-b)",
                "brand-b-secondary": "var(--text-brand-b-secondary)",
                "brand-secondary": "var(--text-brand-secondary)",
                success: "var(--text-success)",
                "success-invert": "var(--text-success-invert)",
                danger: "var(--text-danger)",
                inactive: "var(--text-inactive)",
                "ai-primary": "var(--text-ai-primary)",
                "ai-secondary": "var(--text-ai-secondary)",
                disabled: "var(--text-disabled)",
                frame: "var(--text-frame)",
                "on-tertiary": "var(--text-on-tertiary)",
                // Third party
                facebook: "#1877F2",
                twitterX: "#0F1419",
                "secondary-invert": "var(--text-secondary-invert)",
                warning: "var(--text-warning)",

                // Use primitive classes to bypass the token and directly reference the primitive.
                // *Note* Use only as escape hatch. Ex: text color should remain same in dark mode
                "primitive--white": "var(--white)",

                // AI info styles
                "ai-info": "#240047",
                "ai-info-light": "#5B2A8A",
                "ai-info-lighter": "#E5D3EF"
            },
            // Border colors. Ex: border-stroke-secondary
            stroke: {
                transparent: "transparent",
                primary: "var(--border-primary)",
                secondary: "var(--border-secondary)",
                "secondary-invert": "var(--border-secondary-invert)",
                tertiary: "var(--border-tertiary)",
                quaternary: "var(--border-quaternary)",
                brand: "var(--border-brand)",
                "brand-b": "var(--border-brand-b)",
                ai: "var(--border-ai)",
                prevcare: "var(--border-prevcare)",
                "prevcare-b": "var(--border-prevcare-b)",
                "prevcare-b-secondary": "var(--border-prevcare-b-secondary)",
                danger: "var(--border-danger)",
                success: "var(--border-success)",
                "success-light": "var(--border-success-light)",
                warning: "var(--system-yellow-700)",

                // AI info styles
                "ai-info-light": "#5B2A8A"
            },
            // Background colors. Ex: bg-background-primary
            background: {
                transparent: "transparent",
                overlay: "var(--brand-c-800-alpha)",
                primary: "var(--bg-primary)",
                "primary-invert": "var(--bg-primary-invert)",
                secondary: "var(--bg-secondary)",
                tertiary: "var(--bg-tertiary)",
                quaternary: "var(--bg-quaternary)",
                disabled: "var(--bg-disabled)",
                muted: "var(--bg-muted)",
                "brand-weakest": "var(--bg-brand-weakest)",
                "brand-primary": "var(--bg-brand-primary)",
                "brand-secondary": "var(--bg-brand-secondary)",
                "brand-tertiary": "var(--bg-brand-tertiary)",
                "brand-quaternary": "var(--bg-brand-quaternary)",
                "brand-b-primary": "var(--bg-brand-b-primary)",
                "brand-b-secondary": "var(--bg-brand-b-secondary)",
                "brand-b-tertiary": "var(--bg-brand-b-tertiary)",
                "secondary-invert": "var(--bg-secondary-invert)",
                prevcare: "var(--bg-prevcare)",
                "prevcare-b-primary": "var(--bg-prevcare-b-primary)",
                danger: "var(--bg-danger)",
                success: "var(--bg-success)",
                warning: "var(--bg-warning)",
                "success-secondary": "var(--bg-success-secondary)",
                "danger-secondary": "var(--bg-danger-secondary)",
                frame: "var(--bg-frame)",
                "frame-secondary": "var(--bg-frame-secondary)",
                ai: "var(--bg-ai)",
                "prevcare-b": "var(--bg-prevcare-b)",
                "surface-base": "var(--bg-surface-base)",

                // AI info styles
                "ai-info": "#F9F3FC",

                // Use primitive classes to bypass the token and directly reference the primitive.
                // *Note* Use only as escape hatch. Ex: background color should remain same when in dark mode.
                "primitive--brand-400": "var(--brand-400)"
            }
        },
        spacing: {
            0: "0rem",
            0.5: "0.125rem",
            1: "0.25rem",
            1.5: "0.375rem",
            2: "0.5rem",
            2.5: "0.625rem",
            3: "0.75rem",
            4: "1rem",
            5: "1.25rem",
            6: "1.5rem",
            7: "1.75rem",
            8: "2rem",
            9: "2.25rem",
            10: "2.5rem",
            11: "2.75rem",
            12: "3rem",
            14: "3.5rem",
            16: "4rem",
            20: "5rem",
            22: "5.5rem",
            24: "6rem",
            28: "7rem",
            32: "8rem",
            36: "9rem"
        },
        borderRadius: {
            none: "0rem",
            sm: "0.125rem",
            DEFAULT: "0.25rem",
            md: "0.375rem",
            lg: "0.5rem",
            xl: "0.75rem",
            "2xl": "1rem",
            "3xl": "1.5rem",
            full: "9999rem"
        },
        extend: {
            screens: {
                // custom breakpoints:
                "2xs": "360px",
                xs: "428px"
            },
            boxShadow: {
                "faux-border": "0 0 0 1px var(--gray-300)",
                "faux-border-danger": "0 0 0 1px var(--system-red-600)"
            },
            keyframes: {
                "accordion-down": {
                    from: { height: 0 },
                    to: { height: "var(--radix-accordion-content-height)" }
                },
                "accordion-up": {
                    from: { height: "var(--radix-accordion-content-height)" },
                    to: { height: 0 }
                },
                spin: {
                    transform: "rotate(90deg)"
                },
                "bounce-colored": {
                    "0%, 100%": {
                        transform: "translateY(0)",
                        backgroundColor: "#E5D3EF"
                    },
                    "50%": {
                        transform: "translateY(-25%)",
                        backgroundColor: "#5B2A8A"
                    }
                },
                // Ring 1: Expands to -8px
                "heartbeat-sm": {
                    "0%": { inset: "0px", opacity: "0" },
                    "12.86%": { opacity: "0.4" }, // ~360ms
                    "35.71%": { inset: "-4px", opacity: "0" }, // ~1000ms (End of pulse)
                    "100%": { opacity: "0" } // Gap until 2800ms
                },
                // Ring 2: Expands to -16px
                "heartbeat-md": {
                    "0%": { inset: "0px", opacity: "0" },
                    "12.86%": { opacity: "0.4" },
                    "35.71%": { inset: "-8px", opacity: "0" },
                    "100%": { opacity: "0" }
                },
                // Ring 3: Expands to -24px
                "heartbeat-lg": {
                    "0%": { inset: "0px", opacity: "0" },
                    "12.86%": { opacity: "0.4" },
                    "35.71%": { inset: "-12px", opacity: "0" },
                    "100%": { opacity: "0" }
                },
                // Using 'left' ensures it travels the full width of the parent
                "shimmer-button": {
                    "0%": { left: "-30%" },
                    "14.28%": { left: "120%" },
                    "100%": { left: "200%" }
                },
                // The border shimmer goes the opposite way
                "shimmer-border": {
                    "0%": { right: "-30%" },
                    "42.85%": { right: "-30%" },
                    "64.25%": { right: "120%" },
                    "100%": { right: "120%" }
                }
            },
            animation: {
                "accordion-down": "accordion-down 0.2s ease-out",
                "accordion-up": "accordion-up 0.2s ease-out",
                spin: "spin .8s linear infinite",
                "bounce-colored": "bounce-colored 1s ease-in-out infinite",
                "heartbeat-sm": "heartbeat-sm 2.8s ease-out infinite",
                "heartbeat-md": "heartbeat-md 2.8s ease-out infinite",
                "heartbeat-lg": "heartbeat-lg 2.8s ease-out infinite",
                "shimmer-button-loop": "shimmer-button 7s ease-in-out infinite",
                "shimmer-border-loop": "shimmer-border 7s ease-in-out infinite"
            },
            fill: {
                "success-secondary": "var(--bg-success-secondary)"
            }
        }
    },
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    plugins: [require("tailwindcss-animate")]
};
